import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Grid,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import useStyles from '../styles';
import { PlanContext } from '../../context/PlanContext';
import { FundType } from '../../constants';

import { formatCurrency } from '../../../../../shared/helpers/utils';

export const TableLabelTypography = styled(Typography)(({ theme }) => ({
  fontSize: 27,
  fontWeight: 600,
  [theme.breakpoints.down('md')]: {
    fontSize: 16,
    fontWeight: 600,
  },
}));

const StyledTableCell = styled(TableCell)({
  fontSize: '12px',
});

const fundTypeLabels = Object.freeze({
  [FundType.CORE]: 'Core',
  [FundType.FOCUSED]: 'Focused',
  [FundType.FOLLOW_ON]: 'Follow-On',
});

const getFundTypeLabel = (fundType) => {
  const label = fundTypeLabels[fundType];
  if (!label) {
    throw new Error(`Unsupported venture plan fund type: ${fundType}`);
  }
  return label;
};

const ReviewPlan = ({ setNextDisabled }) => {
  const classes = useStyles();
  const {
    plan,
    wordpressCoreFundData,
    wordpressFocusedFundData,
    loading,
    planningConfig,
  } = useContext(PlanContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const wordpressFundData = {
    ...wordpressCoreFundData,
    ...wordpressFocusedFundData,
  };
  const isReady = !loading
    && Object.keys(wordpressCoreFundData).length > 0
    && Object.keys(wordpressFocusedFundData).length > 0
    && Boolean(planningConfig?.reviewPlan);

  useEffect(() => {
    setNextDisabled(!isReady);
  }, [isReady, setNextDisabled]);

  const getAllocation = (fundType) => {
    const allocation = plan?.fundTypeAllocations?.find(
      (candidateAllocation) => candidateAllocation.fundType === fundType,
    );
    return allocation?.amount && parseFloat(allocation.amount) > 0
      ? parseFloat(allocation.amount)
      : '';
  };

  const totalAllocation = [
    getAllocation(FundType.CORE),
    getAllocation(FundType.FOCUSED),
    getAllocation(FundType.FOLLOW_ON),
    getAllocation(FundType.SYNDICATION),
  ].reduce((total, allocation) => total + (parseFloat(allocation) || 0), 0);

  const getAllocationLabels = (
    isAutoDistributionReinvestmentSelected,
    isAutoAnnualReinvestmentSelected,
    fundType,
  ) => {
    const labels = [];
    const planningConfigKeyByFundType = {
      [FundType.CORE]: 'coreFundsPlan',
      [FundType.FOCUSED]: 'focusedFundsPlan',
      [FundType.FOLLOW_ON]: 'followOnFundsPlan',
    };
    const planningConfigByFundType = planningConfig?.[
      planningConfigKeyByFundType[fundType]
    ];
    if (isAutoAnnualReinvestmentSelected === 1) {
      labels.push(planningConfigByFundType?.autoAnnualReinvestmentSummaryLabel);
    }
    if (isAutoDistributionReinvestmentSelected === 1) {
      labels.push(planningConfigByFundType?.autoDistributionReinvestmentSummaryLabel);
    }
    return (
      <div>
        {labels.map((label) => (
          <div key={label}>{label}</div>
        ))}
      </div>
    );
  };

  if (
    loading
    || Object.keys(wordpressCoreFundData).length === 0
    || Object.keys(wordpressFocusedFundData).length === 0
    || !planningConfig?.reviewPlan
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box className={classes.content}>
      <Typography
        variant="h1ExtraLarge"
        component="h1"
        style={{ color: '#191919' }}
      >
        {planningConfig?.reviewPlan?.title}
      </Typography>
      <Typography
        variant="body2Inter"
        component="div"
        style={{ color: '#797979', padding: 15 }}
        dangerouslySetInnerHTML={{
          __html: planningConfig?.reviewPlan?.subtitle?.richtext || '',
        }}
      />
      <Typography
        variant="field"
        component="div"
        style={{
          fontSize: isMobile ? 14 : 18,
          maxWidth: '83.33%',
          marginLeft: 'auto',
          marginRight: 'auto',
          textAlign: 'center',
        }}
        dangerouslySetInnerHTML={{
          __html: planningConfig?.reviewPlan?.bodyCopy?.richtext || '',
        }}
      />

      <Grid
        container
        xs={12}
        sm={10}
        className={classes.card}
        sx={{ paddingTop: 0 }}
      >
        <Box
          sx={{
            width: '100%',
            overflowX: 'auto', // Enables horizontal scrolling
          }}
        >
          <Table>
            <TableHead>
              {!isMobile && (
                <TableRow>
                  <StyledTableCell
                    colSpan={4}
                    align="center"
                    sx={{ borderBottom: 'none' }}
                  >
                    <TableLabelTypography component="h2">
                      Forward 12 Month Plan
                    </TableLabelTypography>
                  </StyledTableCell>
                </TableRow>
              )}
              <TableRow>
                <StyledTableCell sx={{ fontWeight: 'bold' }}>
                  Category
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 'bold' }}>
                  Fund
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 'bold' }}>
                  Allocation
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 'bold' }}>
                  Additional Options
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plan.fundTypeAllocations.map((fundTypeAlloc) => {
                if (fundTypeAlloc.fundType === FundType.SYNDICATION) {
                  return (
                    <TableRow key={fundTypeAlloc.id}>
                      <StyledTableCell>Syndications</StyledTableCell>
                      <StyledTableCell />
                      <StyledTableCell>
                        {formatCurrency(fundTypeAlloc.amount)}
                      </StyledTableCell>
                      <StyledTableCell />
                    </TableRow>
                  );
                }
                return fundTypeAlloc.perFundAllocations
                  .filter((fund) => parseFloat(fund.amount) > 0)
                  .map((fund, index) => (
                    <TableRow key={fund.id}>
                      <StyledTableCell>
                        {index === 0 ? getFundTypeLabel(fundTypeAlloc.fundType) : ''}
                      </StyledTableCell>
                      <StyledTableCell>
                        {wordpressFundData[fund.fundFamilyCode]?.fields
                          ?.fundFamilyDisplayName || fund.fundFamilyCode}
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatCurrency(fund.amount)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {getAllocationLabels(
                          fund.isAutoDistributionReinvestmentSelected,
                          fund.isAutoAnnualReinvestmentSelected,
                          fundTypeAlloc.fundType,
                        )}
                      </StyledTableCell>
                    </TableRow>
                  ));
              })}
              <TableRow>
                <StyledTableCell>
                  <strong>Total Annual Allocation</strong>
                </StyledTableCell>
                <StyledTableCell />
                <StyledTableCell colSpan={2}>
                  <strong>{formatCurrency(totalAllocation)}</strong>
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Grid>
    </Box>
  );
};

export default ReviewPlan;

ReviewPlan.propTypes = {
  setNextDisabled: PropTypes.func.isRequired,
};
